import type {
  CardChart,
  ChartCard,
  ChartSeries,
} from '@/features/dashboard/api/schemas'
import { formatMonthYear } from '@/features/dashboard/format/format'
import { deepDiveKind, isCategoryKind, type DeepDiveKind } from './deepDiveKind'
import { metricSeries, referenceSeries, seriesLabel } from './series'

export type Row = {
  name: string
  /** The series' name on the wire (a benchmark's colour goes by it). */
  key?: string
  data: (number | null)[]
  /** The y axis the row is read on: the primary (left) one if absent. */
  axis?: 'primary' | 'secondary' | null | undefined
  /** How the row is drawn in a dual-axis chart; the chart's own way if absent. */
  render?: 'column' | 'line' | null | undefined
}

/** The category names as the x axis shows them: months formatted, other names as they are. */
export const categoryLabels = (
  card: Pick<ChartCard, 'categories' | 'chartType' | 'table'>,
  chart: CardChart | null | undefined
) => {
  const byCategory = isCategoryKind(deepDiveKind(card, chart))
  return card.categories.map(category =>
    byCategory
      ? category
      : formatMonthYear(category, chart?.dateFormat === 'month')
  )
}

/** The number format of the right-hand axis: the card's own where it names none. */
export const secondaryFormat = (card: ChartCard) =>
  card.secondary?.format ?? card.format

/** What a deep-dive chart plots, derived from its card and the stub's drawing hints. */
export const deepDiveModel = (
  card: ChartCard,
  chart: CardChart | null | undefined
) => {
  const kind = deepDiveKind(card, chart)
  const byCategory = isCategoryKind(kind)
  const stacked =
    chart?.stacked === true && kind !== 'waterfall' && kind !== 'pie'
  const rowOf = (series: ChartSeries): Row => ({
    name: seriesLabel(series.name),
    key: series.name,
    data: card.categories.map((_, i) => series.data[i] ?? null),
    ...(series.axis && { axis: series.axis }),
    ...(series.render && { render: series.render }),
  })
  let metrics: Row[] = metricSeries(card.series).map(rowOf)
  // Benchmarks are lines over time: a chart of categories has none.
  const references: Row[] = byCategory
    ? []
    : referenceSeries(card.series).map(rowOf)
  // A dual-axis chart: some series are read on a second (right) y axis.
  const dual =
    !byCategory &&
    kind !== 'map' &&
    [...metrics, ...references].some(row => row.axis === 'secondary')
  // Each category's sum of the metrics (a benchmark is never part of it). Of a
  // dual-axis chart, only the columns of the primary axis add up.
  const summed = dual
    ? metrics.filter(row => row.axis !== 'secondary' && row.render !== 'line')
    : metrics
  let totals = card.categories.map((_, i) =>
    summed.reduce((sum, series) => sum + (series.data[i] ?? 0), 0)
  )
  let labels = categoryLabels(card, chart)
  if (kind === 'hbar') {
    // Largest first (the stable sort keeps the card's order among equals).
    const order = totals
      .map((total, i) => ({ total, i }))
      .sort((a, b) => b.total - a.total)
      .map(({ i }) => i)
    labels = order.map(i => labels[i] ?? '')
    totals = order.map(i => totals[i] ?? 0)
    metrics = metrics.map(row => ({
      ...row,
      data: order.map(i => row.data[i] ?? null),
    }))
  }
  // 100% columns: only several stacked series have shares, and benchmark lines
  // (in absolute terms) cannot sit on that scale.
  const percent =
    chart?.percentStacked === true &&
    stacked &&
    metrics.length > 1 &&
    references.length === 0 &&
    !dual &&
    (kind === 'bar' || kind === 'area' || kind === 'line')
  // Stacking a line only offsets its strokes: stacked lines are drawn as areas.
  // A map or a combo drawn by the generic chart (its fallback) is bars/areas.
  const drawnAs: Exclude<DeepDiveKind, 'map' | 'combo'> =
    kind === 'map'
      ? 'area'
      : kind === 'combo'
        ? 'bar'
        : percent
          ? 'bar'
          : stacked && kind === 'line'
            ? 'area'
            : kind
  return {
    kind: drawnAs,
    stacked,
    percent,
    dual,
    metrics,
    references,
    labels,
    totals,
    showTotals:
      stacked &&
      chart?.stackedTotals === true &&
      metrics.length > 1 &&
      !percent &&
      !dual,
    // A chart with benchmark lines is Apex's "line" chart of typed series.
    apexType:
      references.length > 0 || dual
        ? ('line' as const)
        : drawnAs === 'pie'
          ? ('donut' as const)
          : drawnAs === 'waterfall' || drawnAs === 'hbar'
            ? ('bar' as const)
            : drawnAs,
    // Apex stacks only columns next to lines, so stacked areas under benchmark
    // lines are stacked here instead: each drawn as its running total.
    stacksItself:
      drawnAs === 'area' && stacked && references.length > 0 && !dual,
  }
}

export type DeepDiveModel = ReturnType<typeof deepDiveModel>

/** Each series as the running total of those up to it (a gap stays a gap). */
const runningTotals = (rows: Row[]): Row[] =>
  rows.map((row, index) => ({
    name: row.name,
    data: row.data.map((value, i) =>
      value === null
        ? null
        : rows
            .slice(0, index + 1)
            .reduce((sum, earlier) => sum + (earlier.data[i] ?? 0), 0)
    ),
  }))

/**
 * How a metric is drawn in a mixed chart: as the row says, else a line on the
 * secondary axis and the chart's own way on the primary.
 */
export const seriesType = (model: Pick<DeepDiveModel, 'kind'>, row: Row) =>
  row.render === 'column'
    ? 'column'
    : row.render === 'line' || row.axis === 'secondary'
      ? 'line'
      : model.kind === 'bar'
        ? 'column'
        : model.kind

/**
 * The series Apex draws. Next to benchmark lines each names its type (Apex
 * mixes types only so); stacked areas under them are drawn largest first, so
 * the smaller ones show in front of the bigger.
 */
export const deepDiveSeries = (
  model: DeepDiveModel
): { name: string; type?: string; data: (number | null)[] }[] => {
  // Delta columns are one series, the columns coloured by sign.
  if (model.kind === 'waterfall') {
    return [{ name: model.metrics[0]?.name ?? '', data: model.totals }]
  }
  const mixed = model.references.length > 0 || model.dual
  const metrics = model.stacksItself
    ? runningTotals(model.metrics).reverse()
    : model.metrics
  return [
    ...metrics.map(row =>
      mixed ? { ...row, type: seriesType(model, row) } : row
    ),
    ...model.references.map(row => ({
      ...row,
      type: row.render === 'column' ? 'column' : 'line',
    })),
  ].map(({ name, data, type }: Row & { type?: string }) => ({
    name,
    data,
    ...(type && { type }),
  }))
}

/** A donut's series is its slices: a negative or missing one has no share. */
export const donutSeries = (model: DeepDiveModel): number[] =>
  model.totals.map(v => Math.max(v, 0))
